import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';

export function PrivacyPage() {
  usePageStatusBar({ background: PAGE_CHROME_OFF_WHITE });
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-theme-off-white dark:bg-theme-bg-dark font-bravos">
      <div className="sticky top-0 z-10 bg-theme-off-white dark:bg-theme-bg-dark border-b border-theme-border dark:border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 -ml-1.5 rounded-xl text-theme-text dark:text-theme-off-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-semibold text-theme-text dark:text-theme-off-white">Privacy Policy</h1>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">
        <div className="space-y-8 text-sm text-theme-text/80 dark:text-theme-off-white/80 leading-relaxed">

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">1. Introduction</h2>
            <p>
              Bravos ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and what choices you have regarding your data when you use the Bravos app and related services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">2. Information We Collect</h2>
            <p className="font-medium text-theme-text dark:text-theme-off-white">Information you provide directly:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Account details: name, email address, and password</li>
              <li>Profile information: profile photo, fitness goals, and role (member or trainer)</li>
              <li>Health & fitness data: workout logs, exercise history, and progress notes</li>
              <li>Communications: messages exchanged with trainers through the App</li>
            </ul>
            <p className="font-medium text-theme-text dark:text-theme-off-white mt-4">Information collected automatically:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Device information: device type, operating system, and unique device identifiers</li>
              <li>Usage data: features accessed, pages viewed, and interaction patterns within the App</li>
              <li>Log data: IP address, access times, and referring URLs</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Create and manage your account</li>
              <li>Provide personalized workout plans, exercise recommendations, and trainer matching</li>
              <li>Enable communication between members and trainers</li>
              <li>Track and display your fitness progress</li>
              <li>Send notifications relevant to your activity and goals</li>
              <li>Improve the App's features and user experience</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">4. Health Data</h2>
            <p>
              We treat health and fitness data with additional care. This includes workout logs, body measurements, and other fitness metrics you choose to record. This data is used solely to deliver and improve your experience within the App and is never sold or shared with advertisers.
            </p>
            <p>You can delete your health data at any time through your account settings.</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">5. Sharing Your Information</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-medium text-theme-text dark:text-theme-off-white">Your trainer:</span> When you connect with a trainer on Bravos, they can see your profile, workout history, and progress relevant to your training relationship.
              </li>
              <li>
                <span className="font-medium text-theme-text dark:text-theme-off-white">Service providers:</span> Trusted third-party providers who assist in operating the App (e.g., cloud hosting, analytics), subject to confidentiality obligations.
              </li>
              <li>
                <span className="font-medium text-theme-text dark:text-theme-off-white">Legal requirements:</span> When required by law, court order, or to protect the rights and safety of our users or the public.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">6. Data Retention</h2>
            <p>
              We retain your personal data for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymize your personal data within 30 days, except where we are required to retain it for legal or compliance purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">7. Your Rights & Choices</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><span className="font-medium text-theme-text dark:text-theme-off-white">Access:</span> Request a copy of the personal data we hold about you</li>
              <li><span className="font-medium text-theme-text dark:text-theme-off-white">Correction:</span> Update inaccurate or incomplete information in your account settings</li>
              <li><span className="font-medium text-theme-text dark:text-theme-off-white">Deletion:</span> Delete your account and associated data at any time</li>
              <li><span className="font-medium text-theme-text dark:text-theme-off-white">Portability:</span> Request an export of your data in a machine-readable format</li>
              <li><span className="font-medium text-theme-text dark:text-theme-off-white">Opt-out:</span> Unsubscribe from marketing communications at any time</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@bravosfit.com" className="text-[var(--ink)] dark:text-[var(--action-primary)] underline underline-offset-2">
                support@bravosfit.com
              </a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">8. Security</h2>
            <p>
              We implement industry-standard security measures to protect your information, including encryption in transit (TLS) and at rest, access controls, and regular security reviews. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">9. Children's Privacy</h2>
            <p>
              Bravos is not intended for users under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete it promptly.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">10. Third-Party Services</h2>
            <p>
              The App may integrate with or link to third-party services (such as authentication providers). This Privacy Policy does not cover the practices of those third parties. We encourage you to review the privacy policies of any third-party services you use.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the effective date at the top of this page. We encourage you to review this policy periodically. Your continued use of the App after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">12. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or how we handle your data, please contact us at{' '}
              <a href="mailto:support@bravosfit.com" className="text-[var(--ink)] dark:text-[var(--action-primary)] underline underline-offset-2">
                support@bravosfit.com
              </a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}

export default PrivacyPage;
